import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import Loader from '../../../components/Loader';
import useAppDispatch from '../../../hooks/useAppDispatch';
import useAppSelector from '../../../hooks/useAppSelector';
import useDateFormatter from '../../../hooks/useDateFormatter';
import {
    getHomeworks,
    HomeworkUploadingPayload,
    uploadHomework,
} from 'src/store/student/homework/homework.actions';
import {
    HomeworkStatusColor,
    ITask,
} from 'src/store/student/homework/homework.types';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { FileUpload } from 'src/components/FileUpload/FileUpload';
import { Toast } from 'primereact/toast';
import './HomeworkPage.scss';
import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { classNames } from 'primereact/utils';
import { validateUrl } from 'src/utils/validateUrl';
import { checkDeadline } from 'src/utils/date/checkDeadline';
import { _notify, NotificationTypes } from 'src/utils/notify';
import { IMaterialLoadingItem } from '../MaterialPage/MaterialPage.types';
import { LegacyModal } from 'src/components/Modal/LegacyModal';
import { FileUploadHeaderTemplateOptions } from 'primereact/fileupload';
import useErrorHandler from '../../../hooks/useErrorHandler';
import { type IFilter, StatisticFilter } from './ui';
import { Tooltip } from 'primereact/tooltip';
import { dateFormatWithTime } from 'src/constants/constants';
import { formatMaterialName } from 'src/api/endpoints/materials';
import { map } from 'lodash';
import { DownloadMaterialFilesButton } from 'src/pages/Materials';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';

export const modalItemTemplate = (file: any, props: any): ReactElement => {
    const formatSize: string = props.formatSize;

    const [fileSize, fileSizeUnits] = formatSize.split(' ');

    return (
        <div className="flex align-items-center justify-content-between">
            <div className="block">
                <span>{file.name}</span>
            </div>
            <div className="flex">
                <Tag
                    value={`${Math.ceil(Number(fileSize))} ${fileSizeUnits}`}
                    severity="warning"
                    className="px-3 py-2"
                    style={{
                        background: '#ffc863',
                        width: '90px',
                    }}
                />
                <div className="ml-2 delete-button-container">
                    {props.removeElement}
                </div>
            </div>
        </div>
    );
};

export const fileLoaderHeader: FC<FileUploadHeaderTemplateOptions> = (
    options: FileUploadHeaderTemplateOptions
): ReactElement => {
    const { className, chooseButton } = options;

    return (
        <div
            className={className + ' bg-transparent flex align-content-center'}
        >
            {chooseButton}
        </div>
    );
};

export const HomeworkPage: FC<AuthorizedUserProps> = (props: AuthorizedUserProps) => {
    // state
    const [buttonLoading, setButtonLoading] = useState(false);
    const [_homeworkLoading, setHomeworkLoading] = useState<
        IMaterialLoadingItem[]
    >([]);
    const [tasks, setTasks] = useState<ITask[]>([]);
    const [filter, setFilter] = useState<IFilter>({
        status: ['all'],
    });
    const [task, setTask] = useState<ITask>();
    const [link, setLink] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [files, setFiles] = useState<File[]>();

    // ref
    const toastRef = useRef<Toast>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // redux
    const { isLoading, hasLoaded, data } = useAppSelector(
        state => state.homeworkReducer
    );
    const dispatch = useAppDispatch();

    // hooks
    const _ = useDateFormatter();
    const errorHandler = useErrorHandler();

    useEffect(() => {
        document.title = 'Домашнее задание';

        if (!hasLoaded) {
            dispatch(getHomeworks())
                .unwrap()
                .catch(error => errorHandler(error));
        }
    }, []);

    useEffect((): void => {
        let tasks: ITask[] = data.tasks;

        if (!filter) {
            return;
        }

        if (!filter.status.includes('all')) {
            tasks = tasks.filter((task: ITask): boolean =>
                filter.status.includes(task.status)
            );
        }

        if (filter.subjectId && filter.subjectId !== 'all') {
            tasks = tasks.filter(
                (task: ITask): boolean =>
                    '' + task.material.subject.id === filter.subjectId
            );
        }

        setTasks(tasks);
    }, [filter]);

    useEffect((): void => {
        setTasks(data.tasks);
        setHomeworkLoading(
            data.tasks.map((task: ITask) => ({
                id: task.id,
                isLoading: false,
            }))
        );
    }, [data.tasks]);

    const sendHomeworkHandler = (): void => {
        if (!task) return;

        setButtonLoading(true);

        const payload: HomeworkUploadingPayload = {
            id: task.id,
            ...(files
                ? {
                      link: null,
                      files: files,
                  }
                : {
                      link: link,
                      files: null,
                  }),
        };

        dispatch(uploadHomework(payload))
            .unwrap()
            .then((): void => {
                setButtonLoading(false);

                _notify(
                    toastRef,
                    {
                        type: NotificationTypes.Notification,
                        content: 'Вы успешно загрузили домашнее задание!',
                    },
                    true
                );
            })
            .catch(error => {
                setButtonLoading(false);

                _notify(toastRef, {
                    type: NotificationTypes.Exception,
                    content: error,
                });
            });

        setTask(undefined);
        setLink('');
        setFiles(undefined);
        setIsModalVisible(false);
    };

    const modalFooter = (
        <div>
            <Button
                label="Отменить"
                className="p-button-link"
                icon="pi pi-times"
                onClick={() => setIsModalVisible(false)}
            />
            <Button
                label="Отправить"
                icon="pi pi-check"
                disabled={
                    !(
                        (link.trim().length && validateUrl(link)) ||
                        files?.length
                    )
                }
                onClick={sendHomeworkHandler}
                loading={buttonLoading}
            />
        </div>
    );

    return (
        <StudentLayout pageName="homework-page" auth={props}>
            <>
                <div className="card mt-24">
                    <div className="header flex align-items-center justify-content-between homework-bg">
                        <div className="title big">Домашнее задание</div>
                    </div>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <div className="content" ref={contentRef}>
                            <div className="text fz-20 bold">
                                Общая статистика
                            </div>
                            <StatisticFilter
                                filter={filter}
                                setFilter={setFilter}
                                data={data}
                            />
                            <div className="text fz-20 bold mt-48">
                                Мои домашние задания
                            </div>
                            <div className="flex filter-container mt-24">
                                <Dropdown
                                    value={filter.subjectId}
                                    className="w-300"
                                    options={[
                                        {
                                            label: 'Все предметы',
                                            value: 'all',
                                        },
                                        ...data.subjects.map(s => ({
                                            label: s.name,
                                            value: s.name,
                                        })),
                                    ]}
                                    onChange={event =>
                                        setFilter(prev => ({
                                            ...prev,
                                            subjectId: event.value,
                                        }))
                                    }
                                    placeholder="Выберите предмет"
                                />
                            </div>
                            <div className="grid">
                                {tasks.length ? (
                                    map(tasks, task => (
                                        <div
                                            className="lg:col-4 md:col-6 col-12 task"
                                            key={task.id.toString()}
                                        >
                                            <div
                                                className={classNames(
                                                    'area material bg flex flex-column justify-content-between h-full',
                                                    {
                                                        passed:
                                                            task.status ===
                                                                'На проверке' ||
                                                            task.status ===
                                                                'Выполнено' ||
                                                            task.status ===
                                                                'Просрочено',
                                                    }
                                                )}
                                            >
                                                <div className="task-container">
                                                    <div className="body">
                                                        <div className="flex justify-content-between">
                                                            <small className="date block mt-0">
                                                                {_(
                                                                    task.createdAt
                                                                )}
                                                            </small>
                                                            <div
                                                                style={{
                                                                    color: HomeworkStatusColor[
                                                                        task
                                                                            .status
                                                                    ],
                                                                }}
                                                            >
                                                                {task.status}
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="name mt-4"
                                                            id={`task-${task.id}`}
                                                        >
                                                            {
                                                                task.material
                                                                    .subject
                                                                    .name
                                                            }
                                                        </div>
                                                        <Tooltip
                                                            target={`#task-${task.id}`}
                                                            position="top"
                                                        >
                                                            {
                                                                task.material
                                                                    .subject
                                                                    .name
                                                            }
                                                        </Tooltip>
                                                        <small className="date mt-24">
                                                            Тема:
                                                        </small>
                                                        <div className="mt-4">
                                                            {formatMaterialName(
                                                                task.material
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="footer mt-24">
                                                        <DownloadMaterialFilesButton
                                                            material={
                                                                task.material
                                                            }
                                                            segment={'homework'}
                                                            label={
                                                                'Скачать задание'
                                                            }
                                                        />
                                                        <br />
                                                        <Button
                                                            label="Отправить"
                                                            className="mt-24"
                                                            onClick={() => {
                                                                setTask(task);
                                                                setIsModalVisible(
                                                                    true
                                                                );
                                                            }}
                                                            disabled={
                                                                task.status ===
                                                                    'На проверке' ||
                                                                task.status ===
                                                                    'Выполнено' ||
                                                                task.status ===
                                                                    'Просрочено'
                                                            }
                                                        />
                                                        <div
                                                            className={classNames(
                                                                'mt-24',
                                                                {
                                                                    'danger-text':
                                                                        checkDeadline(
                                                                            new Date(
                                                                                task.expiresAt
                                                                            ),
                                                                            new Date()
                                                                        ) &&
                                                                        (task.status ===
                                                                            'На пересдаче' ||
                                                                            task.status ===
                                                                                'Не сделано'),
                                                                }
                                                            )}
                                                        >
                                                            Выполнить до:{' '}
                                                            {_(
                                                                task.expiresAt,
                                                                dateFormatWithTime
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="fz-20 px-2">
                                        Здесь пока ничего нет
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <LegacyModal
                    withStyle={false}
                    header="Отправить домашнее задание"
                    visible={isModalVisible}
                    style={{ width: '50vw' }}
                    footer={modalFooter}
                    onHide={() => setIsModalVisible(false)}
                >
                    {task && (
                        <>
                            <small className="date">Предмет:</small>
                            <div className="fz-20 bold">
                                {task.material.subject.name}
                            </div>
                            <small className="date">Тема:</small>
                            <div className="fz-20 bold">
                                {formatMaterialName(task.material)}
                            </div>
                            <form className="mt-48">
                                <small className="date">Вставьте ссылку</small>
                                <div className="field mt-8">
                                    <InputText
                                        disabled={!!files?.length}
                                        className="w-full"
                                        id="link"
                                        placeholder="https://example.com/"
                                        value={link}
                                        onChange={event =>
                                            setLink(event.target.value)
                                        }
                                    />
                                </div>
                                <small className="date">
                                    Или прикрепите файлы
                                </small>
                                <div className="field mt-8">
                                    <FileUpload
                                        disabled={!!link.trim().length}
                                        onSelect={(event: any) =>
                                            setFiles(() => [...event.files])
                                        }
                                        onClear={() => setFiles(undefined)}
                                        onRemove={event => {
                                            setFiles(prev =>
                                                prev?.filter(
                                                    file =>
                                                        file.name !==
                                                        event.file.name
                                                )
                                            );
                                        }}
                                    />
                                </div>
                            </form>
                        </>
                    )}
                </LegacyModal>
                <Toast ref={toastRef} />
            </>
        </StudentLayout>
    );
};
